import { PushToSftpTableDataModel } from "../../../../data-model/pushToSftpDataModel";
import { PushToSftpModalTableColumnKeys } from "../../../../enums/tableColumnKeys";

/**
 * Toggles the selection state for all items on the current page.
 * If all items on the page are selected, it deselects them all.
 * If not all items are selected, it selects them all.
 * Respects the disabled property - disabled items are not toggled.
 *
 * @param startIndex - The start index of the current page
 * @param endIndex - The end index of the current page
 * @param setTableData - The state setter for table data
 */
export const toggleSelectAllOnPage = (
  startIndex: number,
  endIndex: number,
  setTableData: React.Dispatch<
    React.SetStateAction<PushToSftpTableDataModel[]>
  >,
) => {
  setTableData((prevData) => {
    // Get the items on the current page
    const pageItems = prevData.slice(startIndex, endIndex + 1);

    // Filter out disabled items for the selection check
    const selectableItems = pageItems.filter(
      (row) => !row[PushToSftpModalTableColumnKeys.SELECT]?.disabled,
    );

    // Check if all selectable items on the page are currently selected
    const allSelected =
      selectableItems.length > 0 &&
      selectableItems.every(
        (row) => row[PushToSftpModalTableColumnKeys.SELECT]?.isChecked === true,
      );

    // Toggle: if all selected, deselect all; otherwise, select all
    const newCheckedState = !allSelected;

    return prevData.map((row, index) => {
      // Only modify items on the current page and not disabled
      if (
        index >= startIndex &&
        index <= endIndex &&
        !row[PushToSftpModalTableColumnKeys.SELECT]?.disabled
      ) {
        return {
          ...row,
          [PushToSftpModalTableColumnKeys.SELECT]: {
            ...row[PushToSftpModalTableColumnKeys.SELECT],
            isChecked: newCheckedState,
          },
        };
      }
      return row;
    });
  });
};

/**
 * Checks if all items on the current page are selected.
 * Respects the disabled property - disabled items are excluded from the check.
 *
 * @param tableData - The full table data array
 * @param startIndex - The start index of the current page
 * @param endIndex - The end index of the current page
 * @returns true if all selectable items on the current page are selected
 */
export const areAllPageItemsSelected = (
  tableData: PushToSftpTableDataModel[],
  startIndex: number,
  endIndex: number,
): boolean => {
  if (tableData.length === 0) return false;

  const pageItems = tableData.slice(startIndex, endIndex + 1);
  if (pageItems.length === 0) return false;

  // Filter out disabled items
  const selectableItems = pageItems.filter(
    (row) => !row[PushToSftpModalTableColumnKeys.SELECT]?.disabled,
  );

  if (selectableItems.length === 0) return false;

  return selectableItems.every(
    (row) => row[PushToSftpModalTableColumnKeys.SELECT]?.isChecked === true,
  );
};
